import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';

import {
  ISlaAreaConfig,
  useEliminarSlaArea,
  useFetchSlaArea,
  useGuardarSlaArea,
} from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-configuracion.actions';
import { ITicketArea } from '@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface';

interface Props {
  // Mismo criterio que los árboles: admin global ve todas las áreas,
  // supervisor/configurador solo las suyas (areas-permitidas).
  areasEditables: ITicketArea[];
  isSuperUser: boolean;
  slaGlobalEnabled: boolean;
}

const formatSla = (minutos?: number | null): string => {
  if (minutos == null || Number.isNaN(minutos) || minutos <= 0) return '—';
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

/**
 * Administración del SLA por área (tab Prioridades/SLA de Catálogos), con el
 * mismo patrón que Árbol de Creación / Árbol de Cierre: se elige el área y
 * se ve/edita lo suyo.
 *
 * Dos estados, sin mezclarlos:
 *  - Área SIN SLA propio: tabla de solo lectura con el SLA global que está
 *    usando + botón "Personalizar".
 *  - Área CON SLA propio: switch activo/inactivo + minutos editables por
 *    prioridad + "Volver al SLA global".
 */
const PanelSlaArea = ({ areasEditables, isSuperUser, slaGlobalEnabled }: Props) => {
  const [areaSel, setAreaSel] = useState<string>('');
  // Supervisor: arranca apuntando a su área (igual que los árboles).
  useEffect(() => {
    if (!areaSel && !isSuperUser && areasEditables.length > 0) {
      setAreaSel((areasEditables[0] as any).id_area);
    }
  }, [areaSel, isSuperUser, areasEditables]);

  const { data: configRes, isLoading } = useFetchSlaArea(areaSel || null);
  const config = (configRes as any)?.data as ISlaAreaConfig | undefined;
  const configurado = !!config?.configurado;

  // editando=true muestra el formulario (área ya configurada, o el usuario
  // pulsó "Personalizar" en un área que aún usa el global).
  const [editando, setEditando] = useState(false);
  const [habilitado, setHabilitado] = useState(true);
  const [minutos, setMinutos] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!config) return;
    setEditando(!!config.configurado);
    setHabilitado(config.sla_habilitado ?? true);
    // Valores efectivos del área: su minuto propio o, si no tiene, el global.
    // Así el formulario arranca con lo que hoy aplica, sin placeholders.
    const draft: Record<string, string> = {};
    (config.prioridades ?? []).forEach(p => {
      const efectivo = p.sla_minutos_area ?? p.sla_minutos_global;
      draft[p.prioridad_id] = efectivo != null ? String(efectivo) : '';
    });
    setMinutos(draft);
  }, [config]);

  const guardarMutation = useGuardarSlaArea();
  const eliminarMutation = useEliminarSlaArea();
  const pendiente = guardarMutation.isPending || eliminarMutation.isPending;

  const filas = config?.prioridades ?? [];

  const handleGuardar = () => {
    if (!areaSel) return;
    guardarMutation.mutate({
      areaId: areaSel,
      sla_habilitado: habilitado,
      prioridades: Object.entries(minutos)
        .filter(([, v]) => v !== '' && Number(v) > 0)
        .map(([prioridad_id, v]) => ({ prioridad_id, sla_minutos: Number(v) })),
    });
  };

  const areaBloqueada = !isSuperUser && areasEditables.length <= 1;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6">SLA por Área</Typography>
          <Typography variant="caption" color="text.secondary">
            Cada área puede tener su propio SLA. Un área sin SLA propio usa el global de arriba.
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Área"
          value={areaSel}
          onChange={e => setAreaSel(e.target.value)}
          sx={{ minWidth: 280 }}
          disabled={areaBloqueada}
          helperText={
            !areaSel
              ? 'Selecciona el área a configurar'
              : !slaGlobalEnabled
              ? 'El SLA Global está desactivado: no se calculan plazos en ninguna área'
              : undefined
          }
        >
          {areasEditables.map((a: any) => (
            <MenuItem key={a.id_area} value={a.id_area}>
              {a.nombre}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {areaSel && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {areaSel && !isLoading && config && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          {/* Estado del área + acción principal, en una sola línea */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }} flexWrap="wrap">
            {!editando ? (
              <>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 220 }}>
                  Esta área <b>usa el SLA global</b>: los plazos de abajo son los de cada prioridad.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setEditando(true)}
                >
                  Personalizar SLA del área
                </Button>
              </>
            ) : (
              <>
                <Switch
                  checked={habilitado}
                  onChange={() => setHabilitado(!habilitado)}
                  color="success"
                  disabled={pendiente}
                />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 220 }}>
                  {habilitado ? (
                    <>
                      <b>SLA activo en esta área</b> con los minutos de abajo.
                    </>
                  ) : (
                    <>
                      <b>SLA desactivado</b>: los tickets de esta área no generan plazo.
                    </>
                  )}
                </Typography>
              </>
            )}
          </Stack>

          {/* Tabla de prioridades: lectura (global) o edición (minutos del área) */}
          {(!editando || habilitado) && (
            <Box>
              <Stack direction="row" spacing={2} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" fontWeight={700} sx={{ width: 170 }}>
                  Prioridad
                </Typography>
                <Typography variant="caption" fontWeight={700} sx={{ width: 120 }}>
                  SLA global
                </Typography>
                {editando && (
                  <Typography variant="caption" fontWeight={700} sx={{ width: 220 }}>
                    SLA de esta área (minutos)
                  </Typography>
                )}
              </Stack>

              {filas.map(p => {
                const valor = minutos[p.prioridad_id] ?? '';
                return (
                  <Stack
                    key={p.prioridad_id}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ py: 0.75, borderBottom: '1px dashed', borderColor: 'divider' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 170 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: p.color || '#9e9e9e',
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" noWrap>
                        {p.nombre}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ width: 120 }}>
                      {formatSla(p.sla_minutos_global)}
                    </Typography>
                    {editando && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 220 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={valor}
                          onChange={e =>
                            setMinutos(prev => ({ ...prev, [p.prioridad_id]: e.target.value }))
                          }
                          disabled={pendiente}
                          sx={{ width: 110 }}
                          inputProps={{ min: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          = {formatSla(Number(valor))}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                );
              })}
            </Box>
          )}

          {/* Acciones del modo edición */}
          {editando && (
            <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2 }}>
              {!configurado && (
                <Button size="small" onClick={() => setEditando(false)} disabled={pendiente}>
                  Cancelar
                </Button>
              )}
              {configurado && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => eliminarMutation.mutate(areaSel)}
                  disabled={pendiente}
                >
                  Volver al SLA global
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleGuardar}
                disabled={pendiente}
              >
                Guardar
              </Button>
            </Stack>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default PanelSlaArea;
